// @givmo/app-charity — the `/donations` and `/donations/grants/$grantId` route bodies (CPA-WEB11,
// re-homed by CPA-WEB40 — the module landing is the certified statement, §2 ruling 2).
//
// CPA-BE16 retired the previous three-card page and the `VITE_CHARITY_STATEMENT_V2_ENABLED` knob that
// selected between them, so these bodies no longer choose: `/donations` is the unified statement and
// the drill-down is its deep link. The router stays thin the way it always did — `routes.tsx` names a
// component, and what that component renders lives with the feature.

import { Navigate, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { GRANTS_ROUTES } from './grants-routes';
import { StatementPage } from './statement-page';
import {
  parseStatementSearch,
  toStatementSearch,
  type StatementSearchParams,
} from './statement-search';

/** `/donations` — the unified statement. */
export function GrantsScreen() {
  return <StatementScreen />;
}

export interface StatementScreenProps {
  /** Preview-only seam; production omits it and StatementPage reads the ordinary env gate. */
  fundraiserPanelsEnabled?: boolean;
}

/** Exported for the preview harness, which mounts the judged panel configuration directly. */
export function StatementScreen({ fundraiserPanelsEnabled }: StatementScreenProps = {}) {
  const navigate = useNavigate();
  const params = useSearch({ strict: false }) as StatementSearchParams;
  return (
    <StatementPage
      search={parseStatementSearch(params)}
      fundraiserPanelsEnabled={fundraiserPanelsEnabled}
      onSearchChange={(next) =>
        // `replace` deliberately: a toolbar is not navigation. Pushing a history entry per keystroke
        // and per chip would make the browser's back button walk the charity backwards through its own
        // typing instead of returning to the screen it came from. `resetScroll: false` for the same
        // reason: the router scrolls the window to 0,0 after every committed location unless told
        // otherwise (router-core `_scroll.next = resetScroll ?? true`), which yanked a phone reading
        // the rows back to the top on every keystroke and undid the `?grant=` deep-link landing when
        // the consumed param was dropped (CPA-WEB36).
        void navigate({
          to: GRANTS_ROUTES.statement,
          search: toStatementSearch(next),
          replace: true,
          resetScroll: false,
        })
      }
    />
  );
}

/**
 * `/donations/grants/$grantId` — the drill-down has been a real, deep-linkable route since CPA-WEB5
 * (at `/grants/$grantId`, which now replace-redirects here), so it must keep working rather than 404
 * once the page it used to render is gone. It redirects into the by-grant view with that grant opened
 * and scrolled to: the same question, answered on the screen that now owns the answer. `replace` keeps
 * the redirect out of the back button.
 */
export function GrantDetailScreen() {
  return <GrantDetailRedirect />;
}

/** Exported for the preview harness (see StatementScreen). */
export function GrantDetailRedirect() {
  const { grantId } = useParams({ strict: false }) as { grantId?: string };
  return (
    <Navigate
      to={GRANTS_ROUTES.statement}
      search={{ tab: 'grant', ...(grantId ? { grant: grantId } : {}) } as StatementSearchParams}
      replace
    />
  );
}
